/**
 * Route-level head metadata helpers.
 *
 * Every route (static or dynamic) must ship a unique title/description plus
 * matching Open Graph + Twitter tags in the SSR HTML, so social crawlers never
 * fall back to the generic sitewide defaults. `useSEO` refines the same tags on
 * the client during SPA navigation (it adopts these nodes rather than
 * duplicating them).
 */
import { titleCaseFromSlug } from "./entities";
import { seoFor, type SeoTopic } from "./i18n/keywordPools";
import { uiText } from "./i18n/uiText";

export interface RouteSocial {
  title: string;
  description: string;
  /** Optional social-only headline; falls back to `title`. */
  ogTitle?: string;
  /** Optional social-only summary; falls back to `description`. */
  ogDescription?: string;
  /** Open Graph type; defaults to "website". */
  ogType?: "website" | "article";
  /** Absolute social image URL. Defaults to the sitewide branded card. */
  image?: string;
  imageAlt?: string;
  noindex?: boolean;
  /** Keyword-pool topics for this page; emits a short keywords tag. */
  seo?: SeoTopic;
  /** Hand-authored page keywords; take priority over `seo` topics. */
  keywords?: string;
}

export const SOCIAL_IMAGE = "https://statlign.com/og-image.jpg";

/** Meta entries for a route `head()` — title + description + og/twitter pair. */
export function socialMeta(options: RouteSocial): Array<Record<string, string>> {
  const { ogType, image, noindex } = options;
  const title = uiText(options.title);
  const description = uiText(options.description);
  const imageAlt = options.imageAlt ? uiText(options.imageAlt) : undefined;
  const social = options.ogTitle ? uiText(options.ogTitle) : title;
  const socialDesc = options.ogDescription ? uiText(options.ogDescription) : description;
  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: social },
    { property: "og:description", content: socialDesc },
    { name: "twitter:title", content: social },
    { name: "twitter:description", content: socialDesc },
  ];
  meta.push({ property: "og:type", content: ogType ?? "website" });
  if (image) {
    meta.push({ property: "og:image", content: image });
    meta.push({ property: "og:image:secure_url", content: image });
    meta.push({ name: "twitter:image", content: image });
  }
  if (imageAlt) {
    meta.push({ property: "og:image:alt", content: imageAlt });
    meta.push({ name: "twitter:image:alt", content: imageAlt });
  }
  const authored = options.keywords?.split(",").map((k) => k.trim().toLowerCase()).filter(Boolean);
  if (authored?.length) meta.push({ name: "keywords", content: [...new Set([...authored, "statlign"])].slice(0, 10).join(", ") });
  else if (options.seo) meta.push({ name: "keywords", content: seoFor(options.seo).meta });
  if (noindex) meta.push({ name: "robots", content: "noindex, nofollow" });
  return meta;
}

/** "arsenal-vs-chelsea-2026-05-01" -> { name: "Arsenal vs Chelsea", date } */
export function readableFixture(slug: string): { name: string; date: string } {
  const m = slug.match(/^(.+)-vs-(.+?)(?:-(\d{4}-\d{2}-\d{2}))?$/);
  if (!m) return { name: titleCaseFromSlug(slug), date: "" };
  return {
    name: `${titleCaseFromSlug(m[1]!)} vs ${titleCaseFromSlug(m[2]!)}`,
    date: m[3] ?? "",
  };
}

/** Human label for a slug param, e.g. "premier-league" -> "Premier League". */
export function readable(slug: string): string {
  return titleCaseFromSlug(slug);
}
